import React, { useEffect, useState } from "react";
import { Container, Row, Col, Button, Spinner, Alert, Table, Modal, Pagination, Form } from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";
import Layout from "../../../layout/default";
import Block from "../../../components/Block/Block";
import { Link } from "react-router-dom";

const SupplierPanel = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewItemDetails, setViewItemDetails] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [showOtpModal, setShowOtpModal] = useState(false); // For OTP Modal
    const [otp, setOtp] = useState(""); // For OTP input
    const [orderIdForOtp, setOrderIdForOtp] = useState(null); // Store the order ID for OTP verification

    useEffect(() => {
        fetchSupplierOrders();
    }, [currentPage]);

    const fetchSupplierOrders = async () => {
        try {
            const authToken = localStorage.getItem("authToken");
            if (!authToken) {
                Swal.fire({
                    icon: "error",
                    title: "Authentication Error",
                    text: "No authentication token found. Please log in.",
                });
                return;
            }

            const response = await axios.get(
                `${process.env.REACT_APP_HOST}api/orders/supplier`,
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            );

            if (response.status === 200 && response.data.orders) {
                setOrders(response.data.orders || []);
                setTotalPages(response.data.pagination.total_pages || 1);
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Fetch Error",
                    text: "Failed to fetch orders.",
                });
            }
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: "Empty",
                text: "There is no Orders.",
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchOrderItemDetails = async (orderId) => {
        try {
            const authToken = localStorage.getItem("authToken");
            if (!authToken) {
                Swal.fire({
                    icon: "error",
                    title: "Authentication Error",
                    text: "No authentication token found. Please log in.",
                });
                return;
            }

            const response = await axios.get(
                `${process.env.REACT_APP_HOST}api/order-items/admin/${orderId}`,
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            );

            if (response.status === 200 && response.data.data) {
                setViewItemDetails(response.data.data);
                setShowViewModal(true);
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Fetch Error",
                    text: "Failed to fetch order item details.",
                });
            }
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "An error occurred while fetching order item details.",
            });
        }
    };

    const handleStatusChange = async (orderId, newStatus) => {
        try {
            const authToken = localStorage.getItem("authToken");
            if (!authToken) {
                Swal.fire({
                    icon: "error",
                    title: "Authentication Error",
                    text: "No authentication token found. Please log in.",
                });
                return;
            }

            const response = await axios.put(
                `${process.env.REACT_APP_HOST}api/orders/status/${orderId}?order_status=${newStatus}`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            );

            if (response.status === 200) {
                fetchSupplierOrders();
                Swal.fire({
                    icon: "success",
                    title: "Success",
                    text: "Order status updated successfully!",
                });
                if (newStatus === "2") { // If the order status is 'Delivered', trigger OTP verification
                    setOrderIdForOtp(orderId);
                    setShowOtpModal(true);
                }
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Update Error",
                    text: "Failed to update order status.",
                });
            }
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "An error occurred while updating the order status.",
            });
        }
    };

    const handleDeliveredClick = (orderId) => {
        setOrderIdForOtp(orderId);
        setShowOtpModal(true);
    };

    const verifyOtp = async () => {
        try {
            const authToken = localStorage.getItem("authToken");
            if (!authToken) {
                Swal.fire({
                    icon: "error",
                    title: "Authentication Error",
                    text: "No authentication token found. Please log in.",
                });
                return;
            }

            const response = await axios.post(
                `${process.env.REACT_APP_HOST}api/orders/supplier/delivery-otp/${orderIdForOtp}?delivery_otp=${otp}&order_status=2`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            );

            if (response.status == 200) {
                Swal.fire({
                    icon: "success",
                    title: "OTP Verified",
                    text: "Order marked as delivered successfully!",
                });
                setShowOtpModal(false); // Close OTP modal after successful verification
                fetchSupplierOrders(); // Refresh the orders list
            } else {
                Swal.fire({
                    icon: "error",
                    title: "OTP Verification Failed",
                    text: "Invalid OTP. Please try again.",
                });
            }
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: "OTP Verification Failed",
                text: "Invalid OTP. Please try again.",
            });
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <Spinner animation="border" />
            </div>
        );
    }

    return (
        <Layout title="Supplier Orders">
            <Block.HeadContent>
                <ul className="d-flex">
                    <li>
                        <Link to="/shop/auths/supplier-dashboard" className="btn btn-primary btn-md d-md-none">
                            <span>Go To Dashboard</span>
                        </Link>
                    </li>
                    <li>
                        <Link to="/shop/auths/supplier-dashboard" className="btn btn-primary d-none d-md-inline-flex">
                            <span>Go To Dashboard</span>
                        </Link>
                    </li>
                </ul>
            </Block.HeadContent>
            <Container className="mt-4">
                <h1 className="mb-4">Supplier Orders List</h1>
                {Array.isArray(orders) && orders.length === 0 ? (
                    <Alert variant="info" className="text-center">
                        No orders found.
                    </Alert>
                ) : (
                    <>
                        <Table striped bordered hover responsive>
                            <thead>
                                <tr>
                                    <th>Order ID</th>
                                    <th>User Name</th>
                                    <th>Status</th>
                                    <th>Total Amount</th>
                                    <th>Order Date</th>
                                    <th>Address</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map((order) => (
                                    <tr key={order.order_id}>
                                        <td>{order.order_id}</td>
                                        <td>{order.user_name}</td>
                                        <td>
                                            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                                                <div style={{ flex: 1 }}>
                                                    <Form.Control
                                                        as="select"
                                                        value={order.order_status}
                                                        onChange={(e) => handleStatusChange(order.order_id, e.target.value)}
                                                        style={{ width: "100px" }}
                                                    >
                                                        <option value={0}>Ordered</option>
                                                        <option value={1}>Dispatched</option>
                                                        <option value={3}>Cancelled</option>
                                                    </Form.Control>
                                                </div>
                                                <div style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                                                    <Button
                                                        variant="success"
                                                        size="sm"
                                                        onClick={() => handleDeliveredClick(order.order_id)} // Open OTP modal on Delivered click
                                                    >
                                                        Deliver
                                                    </Button>
                                                </div>
                                            </div>
                                        </td>
                                        <td>₹{order.grand_total ? Number(order.grand_total).toFixed(2) : "N/A"}</td>
                                        <td>{new Date(order.OrderDate).toLocaleDateString('en-GB')}</td>
                                        <td>{order.address}</td>
                                        <td>
                                            <Button variant="info" size="sm" onClick={() => fetchOrderItemDetails(order.order_id)}>
                                                View
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        {/* Pagination */}
                        <div className="d-flex justify-content-center mt-4">
                            <Pagination>
                                <Pagination.Prev onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)} />
                                {[...Array(totalPages)].map((_, index) => (
                                    <Pagination.Item key={index + 1} active={index + 1 === currentPage} onClick={() => handlePageChange(index + 1)}>
                                        {index + 1}
                                    </Pagination.Item>
                                ))}
                                <Pagination.Next onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)} />
                            </Pagination>
                        </div>
                    </>
                )}
            </Container>

            {/* OTP Modal */}
            <Modal show={showOtpModal} onHide={() => setShowOtpModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Verify OTP</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group controlId="otp">
                            <Form.Label>Enter OTP</Form.Label>
                            <Form.Control
                                type="text"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                maxLength="6"
                                placeholder="Enter 6-digit OTP"
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowOtpModal(false)}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={verifyOtp}>
                        Verify
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* View Modal */}
            <Modal show={showViewModal} onHide={() => setShowViewModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Order Item Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {viewItemDetails.length > 0 ? (
                        viewItemDetails.map((item) => (
                            <div key={item.product_id}>
                                <h5 ><strong>Product Title: {item.product_title}</strong></h5>
                                <img
                                    src={`${process.env.REACT_APP_HOST}storage/${item.image_url}`}
                                    alt={item.product_title}
                                    className="rounded me-3"
                                    style={{ width: "120px", height: "120px", objectFit: "cover" }}
                                />
                                <p>Product ID: {item.product_id}</p>
                                <p>Quantity: {item.quantity}</p>
                                <p>Price: ₹{Number(item.price).toFixed(2)}</p>
                                <p>Discount: %{Number(item.discount).toFixed(2)}</p>
                                <p>Total Amount: ₹{Number(item.total_amount).toFixed(2)}</p>
                                <br />
                            </div>
                        ))
                    ) : (
                        <p>No items found for this order.</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowViewModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Layout>
    );
};

export default SupplierPanel;
